import { h, resolveComponent } from 'vue'
import { createRouter, createWebHashHistory } from 'vue-router'
import { pageMetaConfig, getCurrentUrl } from '@/composables/useMeta.js'
import { injectStructuredData } from '@/utils/structuredData.js'

import DefaultLayout from '@/layouts/DefaultLayout'
import RFLayout from '@/layouts/RFLayout'

const routes = [
  {
    path: '/',
    redirect: '/dashboard',
    name: 'Home',
    component: RFLayout,
    children: [
      {
        path: 'dashboard',
        name: 'PerformanceDashboard',
        component: () => 
          import(
            '@/views/clientpanel/dashboard_client.vue'
          ),
        meta: { 
          requiresAuth: true,
          metaKey: 'dashboard'
        },
      },
    ],
  },
  {
    path: '/admin',
    redirect: '/admin/user',
    name: 'Admin',
    component: RFLayout,
    children: [
      {
        path: 'user',
        name: 'User',
        component: () => 
          import(
            '@/views/adminpanel/overview_clients.vue'
          ),
        meta: { 
          requiresAuth: true,
          requiresSuperuser: true,
          metaKey: 'admin-users'
        },
      },
      {
        path: 'admin',
        name: 'AdminOverview',
        component: () => 
          import(
            '@/views/adminpanel/overview_admins.vue'
          ),
        meta: { 
          requiresAuth: true,
          requiresSuperuser: true,
          metaKey: 'admin-admins'
        },
      },
      {
        path: 'portfolios',
        name: 'Portfolios',
        component: () => 
          import(
            '@/views/adminpanel/overview_portfolios.vue'
          ),
        meta: { 
          requiresAuth: true,
          requiresSuperuser: true,
          metaKey: 'admin-portfolios'
        },
      },
      {
        path: 'transactions',
        name: 'Transaktionen',
        component: () => 
          import(
            '@/views/adminpanel/overview_transactions.vue'
          ),
        meta: { 
          requiresAuth: true,
          requiresSuperuser: true,
          metaKey: 'admin-transactions'
        },
      },
      {
        path: 'fees',
        name: 'Fees',
        component: () => 
          import(
            '@/views/adminpanel/overview_fees.vue'
          ),
        meta: { 
          requiresAuth: true,
          requiresSuperuser: true,
          metaKey: 'admin-fees'
        },
      },
      {
        path: 'taxes',
        name: 'Taxes',
        component: () => 
          import(
            '@/views/adminpanel/overview_taxreports.vue'
          ),
        meta: { 
          requiresAuth: true,
          requiresSuperuser: false,
          metaKey: 'admin-taxes'
        },
      },
    ],
  },
  {
    path: '/rf',
    name: 'Rating Framework',
    component: RFLayout,
    redirect: '/rf/dashboard-rf',
    children: [
      {
        path: 'dashboard-rf',
        name: 'Dashboard',
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
          import(
            '@/views/dashboard/RFDashboard.vue'
          ),
        meta: { 
          requiresAuth: true,
          requiresSuperuser: true,
          metaKey: 'rating-framework'
        },
      },
      {
        path: 'rating',
        name: 'Neues Rating',
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
          import(
            '@/views/rating/MainRating.vue'
          ),
        meta: { 
          requiresAuth: true,
          requiresSuperuser: true,
          metaKey: 'rating-new'
        },
      },
      {
        path: 'database-rf',
        name: 'Datenbank',
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
          import(
            '@/views/rating/DBRating.vue'
          ),
        meta: { 
          requiresAuth: true,
          requiresSuperuser: true,
          metaKey: 'rating-database'
        },
      },

    ]
  },
  {
    path: '/pages',
    redirect: '/pages/404',
    name: 'Pages',
    component: {
      render() {
        return h(resolveComponent('router-view'))
      },
    },
    children: [
      {
        path: '404',
        name: 'Page404',
        component: () => import('@/views/pages/Page404'),
      },
      {
        path: '500',
        name: 'Page500',
        component: () => import('@/views/pages/Page500'),
      },
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/views/pages/Login'),
        meta: {
          metaKey: 'login'
        }
      },
      {
        path: 'register',
        name: 'Register',
        component: () => import('@/views/pages/Register'),
        meta: {
          metaKey: 'register'
        }
      },
      {
        path: 'change-pw',
        name: 'Change Password',
        component: () => import('@/views/pages/ChangePW'),
        meta: {
          metaKey: 'change-password'
        }
      },
    ],
  },
]

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    // always scroll to top
    return { top: 0 }
  },
})

router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('accessToken');
  
  // Update meta tags based on the route
  if (to.meta.metaKey && pageMetaConfig[to.meta.metaKey]) {
    const metaData = {
      ...pageMetaConfig[to.meta.metaKey],
      url: getCurrentUrl()
    }
    
    // Update document title
    document.title = metaData.title
    
    // Update meta tags
    setTimeout(() => {
      const updateMeta = (name, content) => {
        let element = document.querySelector(`meta[name="${name}"]`)
        if (!element) {
          element = document.createElement('meta')
          element.setAttribute('name', name)
          document.head.appendChild(element)
        }
        element.setAttribute('content', content)
      }
      
      const updateProperty = (property, content) => {
        let element = document.querySelector(`meta[property="${property}"]`)
        if (!element) {
          element = document.createElement('meta')
          element.setAttribute('property', property)
          document.head.appendChild(element)
        }
        element.setAttribute('content', content)
      }
      
      // Update SEO meta tags
      updateMeta('description', metaData.description)
      updateMeta('keywords', metaData.keywords)
      
      // Update Open Graph meta tags
      updateProperty('og:title', metaData.title)
      updateProperty('og:description', metaData.description)
      updateProperty('og:url', metaData.url)
      updateProperty('og:image', metaData.image || 'https://cmi.cybrgate.ch/CybrgateLogoWithFont.png')
      
      // Update Twitter Card meta tags
      updateMeta('twitter:title', metaData.title)
      updateMeta('twitter:description', metaData.description)
      updateMeta('twitter:url', metaData.url)
      updateMeta('twitter:image', metaData.image || 'https://cmi.cybrgate.ch/CybrgateLogoWithFont.png')
      
      // Update canonical URL if provided
      if (metaData.canonical) {
        let canonicalElement = document.querySelector('link[rel="canonical"]')
        if (!canonicalElement) {
          canonicalElement = document.createElement('link')
          canonicalElement.setAttribute('rel', 'canonical')
          document.head.appendChild(canonicalElement)
        }
        canonicalElement.setAttribute('href', metaData.canonical)
      }

      // Inject structured data (JSON-LD)
      injectStructuredData(to.meta.metaKey)
    }, 0)
  }
  
  // Überprüfe, ob die Route Authentifizierung erfordert
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!token) {
      next({ name: 'Login' }); // Umleitung auf Login-Seite, wenn kein Token vorhanden ist
    } else {
      // Überprüfe, ob die Route Superuser-Rechte benötigt
      if (to.matched.some(record => record.meta.requiresSuperuser)) {
        const isSuperuser = localStorage.getItem('isSuperuser');
        if (isSuperuser === 'true') {
          next(); // Benutzer ist Superuser, also weiter
        } else {
          next({ name: 'Home' }); // Umleitung zur Home-Seite, wenn der Benutzer kein Superuser ist
        }
      } else {
        next(); // Weiter zur geschützten Route, wenn der Benutzer authentifiziert ist
      }
    }
  } else {
    next(); // Weiter zu allen anderen, ungeschützten Seiten
  }
});

export default router
